import makeStyles from '@mui/styles/makeStyles';
import {Video} from "lucide-react";
import {get} from "lodash";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {callDateParts, isCallLive} from "business/upcomingCalls";
import {color, font, type} from "common/tokens";
import Card from "./Card";
import CtaButton from "./CtaButton";

const useStyles = makeStyles({
  card: {
    padding: '30px 30px 32px',
    display: 'flex',
    flexDirection: 'column',
  },
  eyebrow: {
    ...type.eyebrow,
    color: color.TEXT,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: 0,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: color.TEXT,
    flexShrink: 0,
  },
  title: {
    ...type.panelTitle,
    color: color.TEXT,
    margin: '9px 0 0',
  },
  when: {
    ...type.accentDate,
    margin: '9px 0 0',
  },
  empty: {
    padding: '28px 30px 30px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 6,
    background: color.ACCENT_TINT,
  },
  emptyTitle: {
    ...type.cardHeading,
    color: color.TEXT,
    margin: 0,
  },
  emptyBody: {
    ...type.body,
    color: color.ACCENT_TEXT,
    margin: 0,
  },
  emptyActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginTop: 18,
    flexWrap: 'wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 36,
    marginTop: 12,
    flexWrap: 'wrap',
  },
  // Says why Join is dead, and — once it is not — that the link is open. The
  // dot breathes so a live call reads as live without a second glance.
  liveRow: {
    ...type.chipSmall,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: color.TERTIARY,
    marginTop: 10,
  },
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: color.TERTIARY,
    animation: '$breathe 1400ms ease-in-out infinite',
  },
  '@keyframes breathe': {
    '0%, 100%': {opacity: 1},
    '50%': {opacity: 0.3},
  },
  hint: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    margin: '10px 0 0',
  },
  review: {
    marginTop: 16,
    alignSelf: 'flex-start',
  },
});

const NextCallHero = ({call, session, onJoin, onReviewSession, onBookEvent, onViewAllEvents, now}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  // The join window opens ten minutes before the call. Nothing else re-renders
  // this page while the reader waits on it, so without a clock of its own the
  // button stays dead through the moment it should have come alive.
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  // Nothing booked yet: prompt for a call rather than leaving a gap where the
  // hero would be.
  if (!call) {
    return (
      <Card variant={'panel'} className={classes.empty}>
        <p className={classes.emptyTitle}>{t('noCallsScheduled')}</p>
        <p className={classes.emptyBody}>{t('noCallsScheduledBody')}</p>
        <div className={classes.emptyActions}>
          <CtaButton
            size={'large'}
            variant={'primary'}
            label={t('bookAnotherEvent')}
            onClick={onBookEvent}
          />
          {onViewAllEvents && (
            <CtaButton
              size={'large'}
              variant={'secondary'}
              label={t('allEventsTitle')}
              onClick={onViewAllEvents}
            />
          )}
        </div>
      </Card>
    );
  }

  const {short} = callDateParts(call);
  const live = isCallLive(call, now || clock);

  return (
    <Card variant={'panel'} className={classes.card}>
      <p className={classes.eyebrow}>
        <span className={classes.dot}/>
        {t('yourNextMentoringCall')}
      </p>
      <h2 className={classes.title}>{get(call, 'title')}</h2>
      <p className={classes.when}>{short}</p>

      <div className={classes.actions}>
        <CtaButton
          size={'control'}
          variant={live ? 'info' : 'secondary'}
          tone={live ? undefined : 'info'}
          onClick={onJoin}
          disabled={!live}
          icon={<Video size={18} strokeWidth={2}/>}
          label={t('joinCall')}
        />
      </div>

      {live ? (
        <p className={classes.liveRow}>
          <span className={classes.liveDot}/>
          {t('callLinkActive')}
        </p>
      ) : (
        <p className={classes.hint}>{t('callLinkAvailableHint')}</p>
      )}

      {/* Only worth offering before the call: once it is live the material is
          read alongside it, not instead of it. */}
      {!live && Boolean(session) && (
        <CtaButton
          className={classes.review}
          size={'control'}
          variant={'info'}
          label={t('reviewSessionMaterial')}
          onClick={() => onReviewSession(session)}
        />
      )}
    </Card>
  );
};

export default NextCallHero;
